import React, { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';
import { TestResults as TestResultsType } from '@/hooks/useTestRunner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface TestResultsProps {
  results: TestResultsType | null;
  isRunning: boolean;
}

type StatusFilter = 'all' | 'failed' | 'passed';

const TestResults = ({ results, isRunning }: TestResultsProps) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('failed');
  
  if (!results || !results.tests) return null;

  const filteredTests = useMemo(() => {
    if (statusFilter === 'all') return results.tests;
    return results.tests.filter(test => test.status === statusFilter);
  }, [results.tests, statusFilter]);

  const testCounts = useMemo(() => {
    return {
      failed: results.tests.filter(t => t.status === 'failed').length,
      passed: results.tests.filter(t => t.status === 'passed').length,
      total: results.tests.length
    };
  }, [results.tests]);

  return (
    <Card className="p-6 bg-card border-border">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-foreground">Resultados dos Testes</h2>
        <div className="flex gap-2">
          <Button
            variant={statusFilter === 'failed' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('failed')}
            className="relative"
          >
            Apenas Erros
            {testCounts.failed > 0 && (
              <Badge className="ml-2 bg-red-500/20 text-red-400 hover:bg-red-500/20">
                {testCounts.failed}
              </Badge>
            )}
          </Button>
          <Button
            variant={statusFilter === 'passed' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('passed')}
            className="relative"
          >
            Apenas Passando
            {testCounts.passed > 0 && (
              <Badge className="ml-2 bg-green-500/20 text-green-400 hover:bg-green-500/20">
                {testCounts.passed}
              </Badge>
            )}
          </Button>
          <Button
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('all')}
          >
            Todos
            <Badge className="ml-2">
              {testCounts.total}
            </Badge>
          </Button>
        </div>
      </div>

      {filteredTests.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="bg-muted/50 p-6 rounded-full mb-4">
            <CheckCircle2 className="h-12 w-12 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            Nenhum teste encontrado
          </h3>
          <p className="text-sm text-muted-foreground">
            {statusFilter === 'failed' && 'Não há testes com erro.'}
            {statusFilter === 'passed' && 'Não há testes passando.'}
            {statusFilter === 'all' && 'Nenhum teste disponível.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTests.map((test, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-border hover:bg-background/80 transition-colors"
          >
            <div className="flex items-center gap-3 flex-1">
              {test.status === 'passed' ? (
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
              ) : test.status === 'failed' ? (
                <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              ) : (
                <Clock className="h-5 w-5 text-yellow-500 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">{test.name}</p>
                {test.file && (
                  <p className="text-sm text-muted-foreground truncate">{test.file}</p>
                )}
                {test.error && (
                  <p className="text-sm text-red-400 mt-1">{test.error}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {test.duration && (
                <span className="text-sm text-muted-foreground">
                  {test.duration.toFixed(0)}ms
                </span>
              )}
              <Badge
                variant={test.status === 'passed' ? 'default' : 'destructive'}
                className="capitalize"
              >
                {test.status}
              </Badge>
            </div>
          </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default TestResults;
